import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { UserPlus } from "lucide-react";

const emptyForm = {
  full_name: "", phone: "", email: "", national_id: "", date_of_birth: "",
  home_address: "", parish: "", primary_trade: "", additional_trades: "",
  years_experience: "", experience_description: "", tier: "Standard",
  owns_tools: "Yes", reliable_transport: "Yes", emergency_callouts: "Yes",
  has_drivers_license: "No", driving_experience_years: "", can_lift_heavy_weight: "No",
  can_operate_heavy_machinery: "No",
};

const YESNO = ["Yes", "No"];

export const ManualRegistryDialog = ({ API, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [idCard, setIdCard] = useState(null);
  const [resume, setResume] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.full_name.trim() || !form.phone.trim() || !form.parish.trim() || !form.primary_trade.trim()) {
      return toast.error("Name, phone, parish and primary trade are required");
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        years_experience: parseInt(form.years_experience) || 0,
        driving_experience_years:
          form.has_drivers_license === "Yes" ? parseInt(form.driving_experience_years) || 0 : null,
        additional_trades: form.additional_trades
          ? form.additional_trades.split(",").map((t) => t.trim()).filter(Boolean)
          : [],
        available_days: [],
        preferred_work_type: [],
      };
      const fd = new FormData();
      fd.append("data", JSON.stringify(payload));
      if (idCard) fd.append("id_card", idCard);
      if (resume) fd.append("resume", resume);
      await axios.post(`${API}/admin/labour-registry`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Worker added to registry");
      setForm(emptyForm);
      setIdCard(null);
      setResume(null);
      setOpen(false);
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add worker");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="manual-registry-open-btn">
          <UserPlus className="w-4 h-4 mr-1" /> Add Person Manually
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Worker to Labour Registry</DialogTitle>
          <p className="text-sm text-slate-500">Manually added workers are marked as paid and active.</p>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Full Name *</Label>
              <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className="mt-1" data-testid="manual-full-name" />
            </div>
            <div>
              <Label>Phone *</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1" data-testid="manual-phone" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1" data-testid="manual-email" />
            </div>
            <div>
              <Label>National ID</Label>
              <Input value={form.national_id} onChange={(e) => set("national_id", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Date of Birth</Label>
              <Input type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Parish *</Label>
              <Input value={form.parish} onChange={(e) => set("parish", e.target.value)} className="mt-1" data-testid="manual-parish" />
            </div>
          </div>
          <div>
            <Label>Home Address</Label>
            <Input value={form.home_address} onChange={(e) => set("home_address", e.target.value)} className="mt-1" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Primary Trade *</Label>
              <Input value={form.primary_trade} onChange={(e) => set("primary_trade", e.target.value)} placeholder="e.g. Plumbing" className="mt-1" data-testid="manual-primary-trade" />
            </div>
            <div>
              <Label>Additional Trades</Label>
              <Input value={form.additional_trades} onChange={(e) => set("additional_trades", e.target.value)} placeholder="comma separated" className="mt-1" />
            </div>
            <div>
              <Label>Years Experience</Label>
              <Input type="number" min="0" value={form.years_experience} onChange={(e) => set("years_experience", e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Tier *</Label>
              <Select value={form.tier} onValueChange={(v) => set("tier", v)}>
                <SelectTrigger className="mt-1" data-testid="manual-tier"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Standard">Standard</SelectItem>
                  <SelectItem value="Premium">Premium</SelectItem>
                  <SelectItem value="Verified Pro">Verified Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Experience Description</Label>
            <Textarea value={form.experience_description} onChange={(e) => set("experience_description", e.target.value)} rows={2} className="mt-1" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {[
              { k: "owns_tools", l: "Owns Tools" },
              { k: "reliable_transport", l: "Reliable Transport" },
              { k: "emergency_callouts", l: "Emergency Callouts" },
              { k: "has_drivers_license", l: "Driver's License" },
              { k: "can_lift_heavy_weight", l: "Can Lift Heavy Weight" },
              { k: "can_operate_heavy_machinery", l: "Forklift / Heavy Machinery" },
            ].map(({ k, l }) => (
              <div key={k}>
                <Label>{l}</Label>
                <Select value={form[k]} onValueChange={(v) => set(k, v)}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {YESNO.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
            {form.has_drivers_license === "Yes" && (
              <div>
                <Label>Driving Experience (yrs)</Label>
                <Input
                  type="number"
                  min="0"
                  value={form.driving_experience_years}
                  onChange={(e) => set("driving_experience_years", e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>National ID Document</Label>
              <Input type="file" accept="image/*,.pdf,.doc,.docx" onChange={(e) => setIdCard(e.target.files[0] || null)} className="mt-1" data-testid="manual-id-upload" />
            </div>
            <div>
              <Label>Resume / CV</Label>
              <Input type="file" accept="image/*,.pdf,.doc,.docx" onChange={(e) => setResume(e.target.files[0] || null)} className="mt-1" data-testid="manual-resume-upload" />
            </div>
          </div>
          <Button onClick={submit} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600 text-white" data-testid="manual-registry-submit-btn">
            {saving ? "Adding..." : "Add to Registry"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ManualRegistryDialog;
